<template>
    <div>
        <div class="new-main-page">
            <h1 class="new-main-header">
            <img :src="theme.current.headerImage" alt="AI Logo" class="logo" />
            </h1>
            <div class="content">
                <form @submit.prevent="saveChanges">
                    <div class="form-group">
                        <label for="firstName">First name:</label>
                        <div class="field-wrapper">
                            <input
                                type="text"
                                id="firstName"
                                v-model="formData.firstName"
                                @input="validateFirstName"
                                autocomplete="given-name"
                            />
                            <span v-if="errors.firstName" class="error-message">
                                {{ errors.firstName }}
                            </span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="lastName">Last name:</label>
                        <div class="field-wrapper">
                            <input
                                type="text"
                                id="lastName"
                                v-model="formData.lastName"
                                @input="validateLastName"
                                autocomplete="family-name"
                            />
                            <span v-if="errors.lastName" class="error-message">
                                {{ errors.lastName }}
                            </span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="email">Email:</label>
                        <input type="email" id="email" v-model="formData.email" readonly />
                    </div>

                    <div class="form-group">
                        <label for="phoneNumber">Phone number:</label>
                        <div class="field-wrapper">
                            <div class="input-with-button">
                                <input
                                    type="text"
                                    id="phoneNumber"
                                    v-model="phoneNumber"
                                    @input="onPhoneInput"
                                    placeholder="10-digit number"
                                    :disabled="isLoading"
                                />
                                <button
                                    type="button"
                                    class="verify-btn"
                                    @click="sendPhoneVerification"
                                    :disabled="!canSendPhoneVerification"
                                >
                                    Send verification
                                </button>
                            </div>
                            <span
                                v-if="phoneNumberError || phoneConstraintError"
                                class="error-message"
                            >
                                {{ phoneNumberError || phoneConstraintError }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="verificationSent && !isVerified"
                        class="verification-code-container"
                    >
                      <label for="phoneVerificationCode">Verification code</label>
                      <div class="input-with-button">
                        <input
                            type="text"
                            id="phoneVerificationCode"
                            v-model="verificationCode"
                            placeholder="Enter code"
                            :disabled="isVerified || isLoading"
                        />
                        <button
                            type="button"
                            class="verify-btn"
                            @click="verifyPhoneCode"
                            :disabled="!verificationCode || isVerified || isLoading"
                        >
                            Verify
                        </button>
                        <div class="timer">{{ formattedTime }}</div>
                      </div>
                    </div>

                    <div
                        v-if="verificationMessage"
                        :class="[
                            'verification-message',
                            { success: isVerified, error: !isVerified && verificationMessage },
                        ]"
                    >
                        {{ verificationMessage }}
                    </div>
                    
                    <div class="form-group">
                        <label>Profile photo:</label>
                        <div class="profile-photo-container">
                            <div class="avatar-preview">
                            <img
                                v-if="formData.profilePhoto"
                                :src="formData.profilePhoto"
                                alt="Profile Photo"
                                class="profile-photo"
                            />
                            <div v-else class="profile-photo placeholder">
                                <i class="bi bi-person-circle"></i>
                            </div>
                            </div>
                            <div class="avatar-actions">
                            <button
                                type="button"
                                class="change-photo-button"
                                @click="$refs.fileInput.click()"
                            >
                                {{ formData.profilePhoto ? "Change photo" : "Upload photo" }}
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                class="file-input"
                                ref="fileInput"
                                @change="onFileChange"
                            />
                            <p class="photo-hint">
                                Square crop, optimized for chat avatar. JPG/PNG, up to a few MB.
                            </p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label>Reminders:</label>
                        <div class="field-wrapper reminder-boxes">
                            <div class="reminder-field">
                                <label class="field-label">Text Reminders</label>
                                <div class="segmented" role="radiogroup" aria-label="Text Reminders">
                                    <input type="radio" id="text-yes" value="yes" v-model="formData.textReminder" @change="validateReminders" />
                                    <label for="text-yes">Yes</label>

                                    <input type="radio" id="text-no" value="no" v-model="formData.textReminder" @change="validateReminders" />
                                    <label for="text-no">No</label>
                                </div>
                                <p class="reminder-hint">Receive SMS reminders about your coaching sessions.</p>
                            </div>
                            <div class="reminder-field">
                                <label class="field-label">Email Reminders</label>
                                <div class="segmented" role="radiogroup" aria-label="Email Reminders">
                                    <input type="radio" id="email-yes" value="yes" v-model="formData.emailReminder" @change="validateReminders" />
                                    <label for="email-yes">Yes</label>

                                    <input type="radio" id="email-no" value="no" v-model="formData.emailReminder" @change="validateReminders" />
                                    <label for="email-no">No</label>
                                </div>
                                <p class="reminder-hint">Receive email reminders about your coaching sessions.</p>
                            </div>
                        </div>
                    </div>
                    <div v-if="errors.reminder" class="reminder-error">{{ errors.reminder }}</div>
                    <div class="form-group save-button-container">
                        <button
                            type="submit"
                            class="save-button"
                            :disabled="saving || hasValidationErrors"
                        >
                            {{ saving ? "Saving..." : "Save changes" }}
                        </button>
                    </div>
                    <p v-if="saveMessage" class="save-message">{{ saveMessage }}</p>
                    <p v-if="saveError" class="save-error">{{ saveError }}</p>
                </form>
                <!-- Disable peer mentor management temporarily -->
                <!-- <h2>Peer Mentor Management</h2> -->
                <!-- <table class="peer-mentor-table">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Added</th>
                        <th>Delete</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="mentor in peerMentors" :key="mentor.id">
                        <td>{{ mentor.name }}</td>
                        <td>{{ mentor.email }}</td>
                        <td>{{ formatDate(mentor.added) }}</td>
                        <td><button @click="deleteMentor(mentor.id)" class="delete-button">Delete</button></td>
                    </tr>
                    </tbody>
                </table> -->
            </div>
        </div>
    </div>
</template>

<script>
import { useSessionStore } from "@/stores/session";
import { useThemeStore } from "@/stores/theme";
import manageApi from "@/services/manage_api";
import registrationApi from "@/services/registration_api";

export default {
    name: "SettingsPage",
    setup() {
        const theme = useThemeStore();
        const session = useSessionStore();
        return { theme, session };
    },
    data() {
        return {
            formData: {
                firstName: "",
                lastName: "",
                email: "",
                profilePhoto: "",
                textReminder: "no",
                emailReminder: "yes",
            },
            errors: {
                firstName: "",
                lastName: "",
                reminder: "",
            },
            saving: false,
            saveMessage: "",
            saveError: "",
            peerMentors: [],

            phoneNumber: "",
            originalPhoneNumber: "",
            phoneNumberError: "",
            verificationSent: false,
            verificationCode: "",
            verificationUuid: "",
            isVerified: false,
            isLoading: false,
            verificationMessage: "",
            timerSeconds: 0,
            timerInterval: null,
        };
    },
    computed: {
        hasValidationErrors() {
            return (
                !!this.errors.firstName ||
                !!this.errors.lastName ||
                !!this.errors.reminder ||
                !!this.phoneConstraintError
            );
        },
        isValidPhoneNumber() {
            return /^[0-9]{10}$/.test(this.phoneNumber || "");
        },
        canSendPhoneVerification() {
            const phoneChanged = this.phoneNumber && this.phoneNumber !== this.originalPhoneNumber;
            return (
                this.isValidPhoneNumber &&
                phoneChanged &&
                !this.verificationSent &&
                !this.isVerified &&
                !this.isLoading
            );
        },
        formattedTime() {
            const minutes = Math.floor(this.timerSeconds / 60);
            const seconds = this.timerSeconds % 60;
            return `${minutes}:${seconds.toString().padStart(2, "0")}`;
        },
        phoneConstraintError() {
            const smsOn = this.formData.textReminder === "yes";
            const hasNumber = !!this.phoneNumber;

            // Text reminders ON but no phone: not allowed to save
            if (smsOn && !hasNumber) {
                return "Text reminders require a phone number. Enter a phone number or switch Text Reminders to No.";
            }

            // Any number present but not verified yet: not allowed to save
            if (hasNumber && !this.isVerified) {
                return "Please verify your phone number or clear it before saving changes.";
            }

            return "";
        },
    },
    methods: {
        namePattern() {
            // Letters + spaces only
            return /^[A-Za-z\s]+$/;
        },
        validateFirstName() {
            const value = (this.formData.firstName || "").trim();
            if (!value) {
                this.errors.firstName = "First name is required.";
            } else if (!this.namePattern().test(value)) {
                this.errors.firstName = "First name can only contain letters and spaces.";
            } else {
                this.errors.firstName = "";
            }
        },
        validateLastName() {
            const value = (this.formData.lastName || "").trim();
            if (!value) {
                this.errors.lastName = "Last name is required.";
            } else if (!this.namePattern().test(value)) {
                this.errors.lastName = "Last name can only contain letters and spaces.";
            } else {
                this.errors.lastName = "";
            }
        },
        validateReminders() {
            const smsOn = this.formData.textReminder === "yes";
            const emailOn = this.formData.emailReminder === "yes";
            if (!smsOn && !emailOn) {
                this.errors.reminder = "Please choose at least one reminder (text or email).";
                return false;
            }
            this.errors.reminder = "";
            return true;
        },
        normalizePhoneFromSession(raw) {
            if (!raw) return "";
            const digits = String(raw).replace(/\D/g, "");
            if (digits.length === 11 && digits.startsWith("1")) {
                return digits.slice(1);
            }
            if (digits.length === 10) return digits;
            return digits;
        },
        onPhoneInput() {
            // keep only digits, max 10
            const digits = (this.phoneNumber || "").replace(/\D/g, "").slice(0, 10);
            this.phoneNumber = digits;
            this.phoneNumberError = "";

            const matchesOriginal = this.phoneNumber && this.phoneNumber === this.originalPhoneNumber;

            // If they reverted back to original number, restore original verified state
            if (matchesOriginal) {
                this.isVerified = this.session.isPhoneVerified;
                this.verificationSent = false;
                this.verificationCode = "";
                this.verificationUuid = "";
                this.verificationMessage = "";
                this.stopTimer();
            } else {
                // New number (or cleared): force re-verification
                this.isVerified = false;
                this.verificationSent = false;
                this.verificationCode = "";
                this.verificationUuid = "";
                this.verificationMessage = "";
                this.stopTimer();
            }
        },
        startTimer() {
            this.stopTimer();
            this.timerSeconds = 180;
            this.timerInterval = setInterval(() => {
                if (this.timerSeconds > 0) {
                    this.timerSeconds--;
                } else {
                    this.stopTimer();
                    this.verificationSent = false;
                    this.verificationCode = "";
                    this.verificationUuid = "";
                    this.verificationMessage = "Verification code expired. Please try again.";
                }
            }, 1000);
        },
        stopTimer() {
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }
        },
        async sendPhoneVerification() {
            if (!this.isValidPhoneNumber) {
                this.phoneNumberError = "Please enter a valid 10-digit phone number.";
                return;
            }

            this.phoneNumberError = "";
            try {
                this.isLoading = true;
                const response = await registrationApi.sendVerificationCode(
                    `+1${this.phoneNumber}`
                );
                this.verificationUuid = response.session_uuid;
                this.verificationSent = true;
                this.verificationMessage = "Verification code sent! Please check your phone.";
                this.startTimer();
            } catch (error) {
                this.verificationMessage = error?.response?.data?.message || "Failed to send verification code. Please try again.";
            } finally {
                this.isLoading = false;
            }
        },
        async verifyPhoneCode() {
            try {
                this.isLoading = true;
                const result = await registrationApi.verifyCode(
                    this.verificationUuid,
                    this.verificationCode
                );

                if (result.verified) {
                    this.isVerified = true;
                    this.verificationMessage = "Phone number verified successfully!";
                    this.stopTimer();
                } else {
                    this.verificationMessage = "Invalid verification code. Please try again.";
                }
            } catch (error) {
                this.verificationMessage = error?.response?.data?.message || "Failed to verify code. Please try again.";
            } finally {
                this.isLoading = false;
            }
        },
        async saveChanges() {
            this.saveMessage = "";
            this.saveError = "";
            this.validateFirstName();
            this.validateLastName();
            const reminderOk = this.validateReminders();

            if (this.hasValidationErrors || !reminderOk) {
                return;
            }

            const phoneChanged = this.phoneNumber && this.phoneNumber !== this.originalPhoneNumber;

            // Require verification for changed phone number
            if (phoneChanged && !this.isVerified) {
                this.saveError = "Please verify your new phone number before saving changes.";
                return;
            }

            this.saving = true;
            try {
                const payload = {
                    email: this.formData.email,
                    first_name: this.formData.firstName.trim(),
                    last_name: this.formData.lastName.trim(),
                    send_sms: this.formData.textReminder === "yes",
                    send_email: this.formData.emailReminder === "yes",
                    profile_photo: this.formData.profilePhoto || null,
                };

                // Always include phone fields:
                // - If phoneNumber is set -> send +1... and verification flags.
                // - If cleared -> send null so backend can remove it.
                if (this.phoneNumber) {
                    payload.phone_number = `+1${this.phoneNumber}`;
                    payload.phone_verified = this.isVerified;
                    payload.phone_confirmed = true;
                } else {
                    payload.phone_number = null;
                    payload.phone_verified = false;
                }

                const result = await manageApi.editUser(payload);

                if (result?.message === "User successfully updated") {
                    // Sync Pinia session store so chat page shows updated name
                    const patchData = {
                        firstname: payload.first_name,
                        lastname: payload.last_name,
                        sendSms: payload.send_sms,
                        sendEmail: payload.send_email,
                    };
                    if ("phone_number" in payload) {
                        patchData.phoneNumber = payload.phone_number;
                    }

                    this.session.$patch(patchData);

                    if (this.formData.profilePhoto) {
                        this.session.setProfilePhoto(this.formData.profilePhoto);
                    }
                    if (this.isVerified && payload.phone_number) {
                        this.session.markPhoneVerified();
                        this.originalPhoneNumber = this.phoneNumber;
                    }
                    if (!payload.phone_number) {
                        this.originalPhoneNumber = "";
                    }

                    this.saveMessage = "Settings saved.";
                    this.saveError = "";
                } else {
                    this.saveError = result?.message || "Failed to update settings.";
                }
            } catch (err) {
                console.error("Error saving settings:", err);
                this.saveError = "An unexpected error occurred while saving changes.";
            } finally {
                this.saving = false;
            }
        },
        onFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.type.startsWith("image/")) {
                this.saveError = "Please select a valid image file.";
                return;
            }

            const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => {
                        const size = 256;
                        const canvas = document.createElement("canvas");
                        canvas.width = size;
                        canvas.height = size;
                        const ctx = canvas.getContext("2d");

                        // center-crop to square
                        const minSide = Math.min(img.width, img.height);
                        const sx = (img.width - minSide) / 2;
                        const sy = (img.height - minSide) / 2;

                        ctx.drawImage(
                            img,
                            sx,
                            sy,
                            minSide,
                            minSide,
                            0,
                            0,
                            size,
                            size
                        );

                        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
                        this.formData.profilePhoto = dataUrl;
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(file);
        },
        fetchProfilePhoto() {
            const stored = this.session.profilePhoto || this.session.getProfilePhoto;
            if (stored) {
                this.formData.profilePhoto = stored;
            }
        },
        fetchDataFromSession() {
            this.formData.firstName = (this.session.firstname || "").trim();
            this.formData.lastName = (this.session.lastname || "").trim();
            this.formData.email = this.session.username || "";

            const smsOn = this.session.sendSms === true;
            const emailOn = this.session.sendEmail === true;

            this.formData.textReminder = smsOn ? "yes" : "no";
            this.formData.emailReminder = emailOn ? "yes" : "no";

            const rawPhone = this.session.getPhoneNumber || this.session.phoneNumber;
            const normalized = this.normalizePhoneFromSession(rawPhone);
            this.phoneNumber = normalized;
            this.originalPhoneNumber = normalized;
            this.isVerified = this.session.isPhoneVerified;
        },
        // deleteMentor(id) {
        //     // Remove the mentor with the given id
        //     this.peerMentors = this.peerMentors.filter(mentor => mentor.id !== id);
        //     console.log('Mentor deleted:', id);
        // },
        // formatDate(date) {
        //     return date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        // }
    },
    mounted() {
        this.fetchDataFromSession();
        this.fetchProfilePhoto();
        this.validateFirstName();
        this.validateLastName();
        this.validateReminders();
    },
    beforeUnmount() {
        this.stopTimer();
    },
};
</script>

<style scoped>
/* Base typography */
div {
    font-size: 16px;
    color: #333;
}

/* Header logo */
.logo {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
}

/* Layout */
.new-main-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: var(--app-height);
    gap: 12px;
}

.new-main-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    margin: 0;
}

/* Card-style content */
.content {
    padding: 16px;
    background-color: #ffffffb2;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    margin: 20px;
    max-width: 800px;
    width: 95%;
    max-height: 800px;
    overflow-y: auto;
}

/* Form layout */
.form-group {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
}

.form-group label {
    margin-right: 10px;
    width: 180px;
    text-align: left;
    font-weight: bold;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group textarea {
    flex: 1;
    max-width: 380px;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
}

.form-group input[readonly] {
    background-color: #f5f5f5;
    color: #777;
    border-color: #ddd;
    cursor: not-allowed;
}

.field-wrapper {
    flex: 1;
    max-width: 380px;
    display: flex;
    flex-direction: column;
}

/* Profile photo */
.profile-photo-container {
    display: flex;
    align-items: center;
    gap: 16px;
}

.avatar-preview {
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-photo {
    max-width: 100px;
    max-height: 100px;
    height: 100px;
    width: 100px;
    border-radius: 50%;
    object-fit: cover;
}

.profile-photo.placeholder {
    background-color: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-photo.placeholder .bi-person-circle {
    font-size: 40px;
    color: #777;
}

.avatar-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.change-photo-button {
    padding: 6px 12px;
    font-size: 14px;
    color: #fff;
    background-color: var(--primary-color);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 6px;
}

.change-photo-button:hover {
    background-color: #0056b3;
}

.photo-hint {
    font-size: 12px;
    color: #666;
    max-width: 260px;
}

/* Hidden file input */
.file-input {
    display: none;
}

/* Save button */
.save-button-container {
    display: flex;
    justify-content: center;
}

.save-button {
    padding: 10px 20px;
    font-size: 16px;
    color: #fff;
    background-color: var(--primary-color);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: 90%;
}

.save-button[disabled] {
    opacity: 0.7;
    cursor: not-allowed;
}

.save-button:hover:enabled {
    background-color: #0056b3;
}

/* Messages */
.error-message {
    margin-top: 4px;
    font-size: 12px;
    color: #d32f2f;
}

.reminder-error {
    margin-top: -6px;
    margin-bottom: 10px;
    font-size: 13px;
    color: #d32f2f;
}

.save-message {
    margin-top: 8px;
    font-size: 14px;
    color: #2e7d32;
    text-align: center;
}

.save-error {
    margin-top: 8px;
    font-size: 14px;
    color: #d32f2f;
    text-align: center;
}

.reminder-boxes {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    padding-top: 5px; /* Added some top padding for spacing */
}

.reminder-boxes .field-label {
    text-align: center;
}

.segmented {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 5px; /* Added spacing below the segmented control */
}

.segmented input[type="radio"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.segmented label {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    padding: 14px 16px;
    font-size: 14px;
    /* font-weight: 600; */
    min-height: 48px;
    user-select: none;
}

.segmented input[type="radio"]:checked + label {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}

.segmented label:focus,
.segmented input[type="radio"]:focus + label {
    outline: 2px solid #1976d2;
    outline-offset: 2px;
}

.segmented label:active {
    transform: scale(0.98);
}

.reminder-hint {
    font-size: 12px;
    color: #666;
    margin-top: 5px;
}

/* Phone Number */
.input-with-button {
    display: flex;
    align-items: center;
    gap: 10px;
}

.verify-btn {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    min-width: 130px;
    white-space: nowrap;
}

.verify-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.verification-code-container {
    margin-top: 8px;
    margin-bottom: 10px;
}

.timer {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    font-weight: bold;
    color: #ff5722;
}

.verification-message {
    margin: 10px 0;
    padding: 8px;
    border-radius: 4px;
    text-align: center;
}

.verification-message.success {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.verification-message.error {
    background-color: #ffebee;
    color: #c62828;
}

/* Mobile: follow ChatPage mobile feel */
@media (max-width: 768px) {
    .new-main-page {
        --edge-gutter: 8px;
        align-items: stretch;
    }

    .new-main-header {
        margin-left: 0;
        padding: 8px var(--edge-gutter);
    }

    .content {
        margin: 0 var(--edge-gutter) 12px var(--edge-gutter);
        max-width: 100%;
        max-height: none;
        height: auto;
        padding: 12px;
    }

    .form-group {
        flex-direction: column;
        align-items: stretch;
    }

    .form-group label {
        width: 100%;
        margin-bottom: 4px;
    }

    .field-wrapper {
        max-width: 100%;
    }

    .form-group input[type="text"],
    .form-group input[type="email"],
    .form-group textarea {
        max-width: 100%;
    }

    .profile-photo-container {
        flex-direction: column;
        align-items: flex-start;
    }

    .avatar-actions {
        align-items: flex-start;
    }

    .save-button {
        width: 100%;
    }

    .input-with-button {
        flex-direction: column;
    }

    .input-with-button input,
    .verify-btn {
        width: 100%;
    }
}
</style>